import { createContext, useContext } from "react";
import {
  CompatibleSpatialMetricDetailsFragment,
  Geography,
  OverlaySourceDetailsFragment,
  ReportContextSketchClassDetailsFragment,
} from "../../generated/graphql";

export type CardDependenciesValue = {
  metrics: CompatibleSpatialMetricDetailsFragment[];
  sources: OverlaySourceDetailsFragment[];
  loading: boolean;
  geographies: Pick<Geography, "id" | "name" | "translatedProps" | "stableIds">[];
  sketchClass: Pick<
    ReportContextSketchClassDetailsFragment,
    | "id"
    | "projectId"
    | "geometryType"
    | "form"
    | "clippingGeographies"
    | "project"
    | "validChildren"
  > | null;
  errors: { [errorMessage: string]: number };
  /** Errors not attributable to a single metric/source dependency (e.g. query failure). */
  globalErrors: string[];
  /** True while dependency cache was evicted and a fresh payload is in flight. */
  dependenciesAwaitingRefresh: boolean;
  /**
   * When the server could not resolve a metric dependency (e.g. stable id from
   * another project), maps dependencyHash → message so widgets stop waiting for a metric that will never exist.
   */
  dependencyResolutionFailuresByHash: { [dependencyHash: string]: string };
};

export const CardDependenciesContext = createContext<CardDependenciesValue>({
  metrics: [],
  sources: [],
  loading: true,
  geographies: [],
  sketchClass: null,
  errors: {},
  globalErrors: [],
  dependenciesAwaitingRefresh: false,
  dependencyResolutionFailuresByHash: {},
});

/**
 * Hook to access card-level dependencies from within widgets.
 * This provides metrics and sources scoped to the current card,
 * preventing unnecessary re-renders when other cards' data changes.
 */
export function useCardDependenciesContext() {
  return useContext(CardDependenciesContext);
}
